import React, { useState, useEffect, useMemo } from "react";
import { Container, Card, Table, Badge, Row, Col, Alert, Button, ButtonGroup, Form } from "react-bootstrap";
import { collection, getDocs, doc, setDoc, getDoc } from "firebase/firestore";
import { db, callXeroFunction } from "../firebase.js";
import { useAuth } from "../context/AuthContext.js";
import { useLanguage } from "../context/LanguageContext.js";
import { t } from "../utils/translations.js";
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, format, addWeeks, subWeeks, addMonths, subMonths, addYears, subYears, parseISO } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";

export default function RevenuePage() {
  const { user } = useAuth();
  const { language } = useLanguage();

  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("weekly");
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentYear, setCurrentYear] = useState(new Date());
  const [paymentData, setPaymentData] = useState({});
  const [localNotes, setLocalNotes] = useState({});

  // Load clients and jobs
  useEffect(() => {
    loadData();
  }, []);

  // Load payment data when period changes
  useEffect(() => {
    loadPaymentData();
  }, [viewMode, currentWeekStart, currentMonth, currentYear]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load clients from PostgreSQL
      const response = await callXeroFunction('getClients', {}, { method: 'GET' });
      const postgresClients = response.clients || [];

      const clientsData = postgresClients.map(client => ({
        ...client,
        defaultInvoiceAmount: client.default_price || client.defaultInvoiceAmount,
        paymentMethod: client.payment_method || client.paymentMethod
      }));
      setClients(clientsData);

      // Load jobs from Firestore
      const jobsSnap = await getDocs(collection(db, "jobs"));
      const jobsData = jobsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setJobs(jobsData);

    } catch (error) {
      console.error("Error loading data:", error);
      alert(t(language, "revenueErrorLoading"));
    } finally {
      setLoading(false);
    }
  };

  const getPeriodId = () => {
    switch (viewMode) {
      case "yearly": return format(currentYear, "yyyy");
      case "monthly": return format(currentMonth, "yyyy-MM");
      case "weekly": return format(currentWeekStart, "yyyy-MM-dd");
      default: return "";
    }
  };

  const loadPaymentData = async () => {
    try {
      const periodId = getPeriodId();
      const docRef = doc(db, "cashPayments", periodId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const payments = docSnap.data().payments || {};
        setPaymentData(payments);
        const notes = {};
        Object.keys(payments).forEach(clientId => {
          notes[clientId] = payments[clientId].note || "";
        });
        setLocalNotes(notes);
      } else {
        setPaymentData({});
        setLocalNotes({});
      }
    } catch (error) {
      console.error("Error loading payment data:", error);
      setPaymentData({});
      setLocalNotes({});
    }
  };

  const savePaymentData = async (clientId, data) => {
    try {
      const periodId = getPeriodId();
      const newPaymentData = { ...paymentData, [clientId]: data };

      await setDoc(doc(db, "cashPayments", periodId), {
        payments: newPaymentData,
        periodId,
        viewMode,
        updatedAt: new Date().toISOString()
      });

      setPaymentData(newPaymentData);
    } catch (error) {
      console.error("Error saving payment data:", error);
      alert(t(language, "revenueErrorSaving"));
    }
  };

  const handleReceivedChange = (clientId, received) => {
    const currentData = paymentData[clientId] || { received: false, note: "" };
    const currentNote = localNotes[clientId] || "";
    savePaymentData(clientId, { ...currentData, received, note: currentNote });
  };

  const handleNoteChange = (clientId, note) => {
    setLocalNotes(prev => ({ ...prev, [clientId]: note }));
  };

  const handleNoteBlur = (clientId) => {
    const currentData = paymentData[clientId] || { received: false, note: "" };
    const note = localNotes[clientId] || "";
    savePaymentData(clientId, { ...currentData, note });
  };

  // Helper para encontrar cliente
  const findClient = (clientId, clientName) => {
    if (!clientId) return null;
    let client = clients.find(c => String(c.id) === String(clientId));
    if (!client) client = clients.find(c => c.firebase_id === clientId);
    if (!client && clientName) client = clients.find(c => c.name === clientName);
    return client;
  };

  // Period boundaries with useMemo
  const { periodStart, periodEnd } = useMemo(() => {
    let s, e;
    switch (viewMode) {
      case "yearly": s = startOfYear(currentYear); e = endOfYear(currentYear); break;
      case "monthly": s = startOfMonth(currentMonth); e = endOfMonth(currentMonth); break;
      case "weekly": default: s = currentWeekStart; e = endOfWeek(currentWeekStart, { weekStartsOn: 1 }); break;
    }
    return { periodStart: s, periodEnd: e };
  }, [viewMode, currentWeekStart, currentMonth, currentYear]);

  // Filter jobs for current period
  const periodJobs = useMemo(() =>
    jobs.filter(job => {
      const jobDate = parseISO(job.date);
      return jobDate >= periodStart && jobDate <= periodEnd;
    }),
    [jobs, periodStart, periodEnd]
  );

  // Calculate revenue
  const revenue = useMemo(() => {
    const revenueByClient = {};

    periodJobs.forEach(job => {
      const client = findClient(job.clientId, job.clientName);
      if (!client) return;

      const amount = parseFloat(job.invoiceAmount || client.defaultInvoiceAmount) || 0;
      const paymentMethod = client.paymentMethod || "cash";

      if (!revenueByClient[client.id]) {
        revenueByClient[client.id] = {
          client, xeroAmount: 0, cashAmount: 0, jobCount: 0, jobDates: [], teams: []
        };
      }

      revenueByClient[client.id].jobCount += 1;
      revenueByClient[client.id].jobDates.push(job.date);

      if (job.team && !revenueByClient[client.id].teams.includes(job.team)) {
        revenueByClient[client.id].teams.push(job.team);
      }

      if (paymentMethod === "xero") {
        revenueByClient[client.id].xeroAmount += amount;
      } else {
        revenueByClient[client.id].cashAmount += amount;
      }
    });

    return Object.values(revenueByClient).sort((a, b) => {
      const teamComparison = (a.team || "").localeCompare(b.team || "");
      if (teamComparison !== 0) return teamComparison;
      const aOldestDate = a.jobDates.sort()[0];
      const bOldestDate = b.jobDates.sort()[0];
      return aOldestDate.localeCompare(bOldestDate);
    });
  }, [periodJobs, clients]);

  // Group revenue by team
  const revenueByTeam = useMemo(() => {
    const teamGroups = {};

    periodJobs.forEach(job => {
      const client = findClient(job.clientId, job.clientName);
      if (!client) return;

      const team = job.team || "No Team";
      const amount = parseFloat(job.invoiceAmount || client.defaultInvoiceAmount) || 0;
      const paymentMethod = client.paymentMethod || "cash";

      if (!teamGroups[team]) teamGroups[team] = {};
      if (!teamGroups[team][client.id]) {
        teamGroups[team][client.id] = { client, xeroAmount: 0, cashAmount: 0, jobCount: 0, jobDates: [] };
      }

      teamGroups[team][client.id].jobCount += 1;
      teamGroups[team][client.id].jobDates.push(job.date);

      if (paymentMethod === "xero") {
        teamGroups[team][client.id].xeroAmount += amount;
      } else {
        teamGroups[team][client.id].cashAmount += amount;
      }
    });

    const result = {};
    Object.keys(teamGroups).forEach(team => {
      result[team] = Object.values(teamGroups[team]).sort((a, b) => {
        const aOldest = a.jobDates.sort()[0];
        const bOldest = b.jobDates.sort()[0];
        return aOldest.localeCompare(bOldest);
      });
    });

    return result;
  }, [periodJobs, clients]);

  // Calculate totals
  const { totalXero, totalCash, grandTotal, gstAmount } = useMemo(() => {
    const xero = revenue.reduce((sum, item) => sum + item.xeroAmount, 0);
    const cash = revenue.reduce((sum, item) => sum + item.cashAmount, 0);
    return { totalXero: xero, totalCash: cash, grandTotal: xero + cash, gstAmount: xero * 0.15 };
  }, [revenue]);

  // Team totals
  const teamTotals = useMemo(() => {
    const tt = {};
    periodJobs.forEach(job => {
      const client = findClient(job.clientId, job.clientName);
      if (!client) return;
      const team = job.team || "No Team";
      const amount = parseFloat(job.invoiceAmount || client.defaultInvoiceAmount) || 0;
      const paymentMethod = client.paymentMethod || "cash";
      if (!tt[team]) tt[team] = { xero: 0, cash: 0, total: 0 };
      if (paymentMethod === "xero") tt[team].xero += amount;
      else tt[team].cash += amount;
      tt[team].total += amount;
    });
    return tt;
  }, [periodJobs, clients]);

  // Clients without amount
  const clientsWithoutAmount = useMemo(() => {
    const clientIdsWithJobs = revenue.map(item => item.client.id);
    return clients.filter(c =>
      clientIdsWithJobs.includes(c.id) &&
      (!c.defaultInvoiceAmount || c.defaultInvoiceAmount === 0)
    );
  }, [revenue, clients]);

  // Navigation
  const goToPrevious = () => {
    switch (viewMode) {
      case "yearly": setCurrentYear(prev => subYears(prev, 1)); break;
      case "monthly": setCurrentMonth(prev => subMonths(prev, 1)); break;
      case "weekly": setCurrentWeekStart(prev => subWeeks(prev, 1)); break;
    }
  };

  const goToNext = () => {
    switch (viewMode) {
      case "yearly": setCurrentYear(prev => addYears(prev, 1)); break;
      case "monthly": setCurrentMonth(prev => addMonths(prev, 1)); break;
      case "weekly": setCurrentWeekStart(prev => addWeeks(prev, 1)); break;
    }
  };

  const goToCurrent = () => {
    const now = new Date();
    switch (viewMode) {
      case "yearly": setCurrentYear(now); break;
      case "monthly": setCurrentMonth(now); break;
      case "weekly": setCurrentWeekStart(startOfWeek(now, { weekStartsOn: 1 })); break;
    }
  };

  const getPeriodLabel = () => {
    const locale = language === "pt" ? ptBR : enUS;
    switch (viewMode) {
      case "yearly": return format(currentYear, "yyyy", { locale });
      case "monthly": return format(currentMonth, "MMMM yyyy", { locale });
      case "weekly":
        const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
        return `${format(currentWeekStart, "dd/MM/yyyy", { locale })} - ${format(weekEnd, "dd/MM/yyyy", { locale })}`;
      default: return "";
    }
  };

  const locale = language === "pt" ? ptBR : enUS;

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">
        {t(language, "revenueTitle")}
      </h2>

      {/* View Mode Selector */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="align-items-center">
            <Col xs={12} md={6} className="mb-3 mb-md-0">
              <ButtonGroup>
                <Button
                  variant={viewMode === "yearly" ? "primary" : "outline-primary"}
                  onClick={() => setViewMode("yearly")}
                >
                  {t(language, "revenueAnnual")}
                </Button>
                <Button
                  variant={viewMode === "monthly" ? "primary" : "outline-primary"}
                  onClick={() => setViewMode("monthly")}
                >
                  {t(language, "revenueMonthly")}
                </Button>
                <Button
                  variant={viewMode === "weekly" ? "primary" : "outline-primary"}
                  onClick={() => setViewMode("weekly")}
                >
                  {t(language, "revenueWeekly")}
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Period Navigation */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="align-items-center">
            <Col xs={12} md={6} className="mb-3 mb-md-0">
              <h4 className="mb-0">{getPeriodLabel()}</h4>
            </Col>
            <Col xs={12} md={6}>
              <div className="d-flex gap-2 justify-content-md-end">
                <Button variant="outline-secondary" onClick={goToPrevious}>
                  ← {t(language, "revenuePrevious")}
                </Button>
                <Button variant="outline-primary" onClick={goToCurrent}>
                  {t(language, "revenueCurrent")}
                </Button>
                <Button variant="outline-secondary" onClick={goToNext}>
                  {t(language, "revenueNext")} →
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Clients without amount warning */}
      {clientsWithoutAmount.length > 0 && (
        <Alert variant="warning" className="mb-4">
          <Alert.Heading>
            {t(language, "revenueNoAmountWarning")}
          </Alert.Heading>
          <p className="mb-2">
            {t(language, "revenueNoAmountDesc")}
          </p>
          <div className="d-flex flex-wrap gap-2">
            {clientsWithoutAmount.map(client => (
              <Badge key={client.id} bg="warning" text="dark" className="fs-6">
                {client.name}
              </Badge>
            ))}
          </div>
        </Alert>
      )}

      {/* Team Summary Cards */}
      {Object.keys(teamTotals).length > 0 && (
        <>
          <h4 className="mb-3">{t(language, "revenueByTeam")}</h4>
          <Row className="mb-4">
            {Object.keys(teamTotals).sort().map(team => (
              <Col md={4} key={team} className="mb-3">
                <Card className="shadow-sm" style={{ backgroundColor: "#f5f5f5", border: "2px solid #757575" }}>
                  <Card.Body>
                    <h5 className="mb-3 fw-bold" style={{ color: "#424242" }}>
                      {team}
                    </h5>
                    <Row>
                      <Col xs={6}>
                        <div className="text-center p-2" style={{ backgroundColor: "#e3f2fd", borderRadius: "8px" }}>
                          <small className="text-muted d-block">{t(language, "revenueBankAccount")}</small>
                          <strong style={{ color: "#1976d2" }}>${teamTotals[team].xero.toFixed(2)}</strong>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-center p-2" style={{ backgroundColor: "#e8f5e9", borderRadius: "8px" }}>
                          <small className="text-muted d-block">Cash</small>
                          <strong style={{ color: "#388e3c" }}>${teamTotals[team].cash.toFixed(2)}</strong>
                        </div>
                      </Col>
                    </Row>
                    <div className="text-center mt-3 p-2" style={{ backgroundColor: "#fff3e0", borderRadius: "8px" }}>
                      <small className="text-muted d-block">{t(language, "revenueTotal")}</small>
                      <h4 className="mb-0 fw-bold" style={{ color: "#f57c00" }}>${teamTotals[team].total.toFixed(2)}</h4>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      {/* Summary Cards */}
      <h4 className="mb-3">{t(language, "revenueOverallTotals")}</h4>
      <Row className="mb-4 g-3">
        <Col xs={6} md={4}>
          <Card className="text-center shadow-sm" style={{ backgroundColor: "#e3f2fd" }}>
            <Card.Body>
              <h6 className="text-muted mb-2">
                {t(language, "revenueBankAccount")}
              </h6>
              <h3 className="mb-0" style={{ color: "#1976d2" }}>
                ${totalXero.toFixed(2)}
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={4}>
          <Card className="text-center shadow-sm" style={{ backgroundColor: "#e8f5e9" }}>
            <Card.Body>
              <h6 className="text-muted mb-2">Cash</h6>
              <h3 className="mb-0" style={{ color: "#388e3c" }}>
                ${totalCash.toFixed(2)}
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card className="text-center shadow-sm" style={{ backgroundColor: "#fff3e0" }}>
            <Card.Body>
              <h6 className="text-muted mb-2">
                {t(language, "revenueTotal")}
              </h6>
              <h3 className="mb-0" style={{ color: "#f57c00" }}>
                ${grandTotal.toFixed(2)}
              </h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* GST Card */}
      {viewMode === "weekly" && totalXero > 0 && (
        <Row className="mb-4">
          <Col md={12}>
            <Card className="text-center shadow-sm" style={{ backgroundColor: "#fce4ec", border: "2px solid #c2185b" }}>
              <Card.Body>
                <h5 className="mb-2" style={{ color: "#c2185b" }}>
                  {t(language, "revenueGstWeek")}
                </h5>
                <h2 className="mb-0 fw-bold" style={{ color: "#c2185b" }}>
                  ${gstAmount.toFixed(2)}
                </h2>
                <small className="text-muted mt-2 d-block">
                  ({t(language, "revenueGstPercent")} ${totalXero.toFixed(2)})
                </small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Detailed Revenue Table */}
      <Card className="shadow-sm">
        <Card.Header>
          <h5 className="mb-0">
            {t(language, "revenueByClient")}
          </h5>
        </Card.Header>
        <Card.Body>
          <Table responsive striped bordered hover>
            <thead className="table-light">
              <tr>
                <th>{t(language, "revenueClient")}</th>
                <th className="text-center">{t(language, "revenueJobs")}</th>
                <th>{t(language, "revenueServiceDates")}</th>
                <th className="text-end">{t(language, "revenueAmount")}</th>
                <th className="text-center">{t(language, "revenueReceived")}</th>
                <th>{t(language, "revenueNote")}</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(revenueByTeam).length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-4">
                    {t(language, "revenueNoJobs")}
                  </td>
                </tr>
              ) : (
                <>
                  {Object.keys(revenueByTeam).sort().map(team => {
                    const teamClients = revenueByTeam[team];
                    const bankAccountClients = teamClients.filter(item => item.client.paymentMethod === "xero");
                    const cashClients = teamClients.filter(item => item.client.paymentMethod !== "xero");

                    const teamXeroTotal = bankAccountClients.reduce((sum, item) => sum + item.xeroAmount, 0);
                    const teamCashTotal = cashClients.reduce((sum, item) => sum + item.cashAmount, 0);
                    const teamTotal = teamXeroTotal + teamCashTotal;

                    return (
                      <React.Fragment key={team}>
                        {/* Team Header */}
                        <tr style={{ backgroundColor: "#e0e0e0" }}>
                          <td colSpan="6" className="fw-bold py-3" style={{ fontSize: "1.1rem" }}>
                            {team} - {t(language, "revenueTotal")}: ${teamTotal.toFixed(2)}
                          </td>
                        </tr>

                        {/* Bank Account Clients */}
                        {bankAccountClients.length > 0 && (
                          <>
                            <tr className="table-info">
                              <td colSpan="6" className="fw-bold ps-4">
                                <Badge bg="primary" className="me-2">{t(language, "revenueBankAccount")}</Badge>
                                {t(language, "revenueBankPayment")}
                              </td>
                            </tr>
                            {bankAccountClients.map((item) => (
                              <tr key={`${team}-xero-${item.client.id}`}>
                                <td className="ps-5">
                                  <strong>{item.client.name}</strong>
                                </td>
                                <td className="text-center">{item.jobCount}</td>
                                <td>
                                  {item.jobDates.map((date, idx) => (
                                    <div key={idx}>
                                      {format(parseISO(date), "dd/MM/yyyy", { locale })}
                                    </div>
                                  ))}
                                </td>
                                <td className="text-end">
                                  <strong>${item.xeroAmount.toFixed(2)}</strong>
                                </td>
                                <td className="text-center text-muted">-</td>
                                <td className="text-muted">-</td>
                              </tr>
                            ))}
                            <tr className="table-primary fw-bold">
                              <td colSpan="3" className="text-end ps-5">
                                {t(language, "revenueBankSubtotal")}
                              </td>
                              <td className="text-end">${teamXeroTotal.toFixed(2)}</td>
                              <td colSpan="2"></td>
                            </tr>
                          </>
                        )}

                        {/* Cash Clients */}
                        {cashClients.length > 0 && (
                          <>
                            <tr className="table-success">
                              <td colSpan="6" className="fw-bold ps-4">
                                <Badge bg="success" className="me-2">Cash</Badge>
                                {t(language, "revenueCashPayment")}
                              </td>
                            </tr>
                            {cashClients.map((item) => {
                              const clientPaymentData = paymentData[item.client.id] || { received: false, note: "" };
                              const localNote = localNotes[item.client.id] || "";
                              return (
                                <tr key={`${team}-cash-${item.client.id}`} style={{ backgroundColor: clientPaymentData.received ? "#d4edda" : "transparent" }}>
                                  <td className="ps-5">
                                    <strong>{item.client.name}</strong>
                                  </td>
                                  <td className="text-center">{item.jobCount}</td>
                                  <td>
                                    {item.jobDates.map((date, idx) => (
                                      <div key={idx}>
                                        {format(parseISO(date), "dd/MM/yyyy", { locale })}
                                      </div>
                                    ))}
                                  </td>
                                  <td className="text-end">
                                    <strong>${item.cashAmount.toFixed(2)}</strong>
                                  </td>
                                  <td className="text-center">
                                    <Form.Check
                                      type="checkbox"
                                      checked={clientPaymentData.received}
                                      onChange={(e) => handleReceivedChange(item.client.id, e.target.checked)}
                                      style={{ transform: "scale(1.2)" }}
                                    />
                                  </td>
                                  <td>
                                    <Form.Control
                                      type="text"
                                      placeholder={t(language, "revenueNotePlaceholder")}
                                      value={localNote}
                                      onChange={(e) => handleNoteChange(item.client.id, e.target.value)}
                                      onBlur={() => handleNoteBlur(item.client.id)}
                                      size="sm"
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="table-success fw-bold">
                              <td colSpan="3" className="text-end ps-5">
                                {t(language, "revenueCashSubtotal")}
                              </td>
                              <td className="text-end">${teamCashTotal.toFixed(2)}</td>
                              <td colSpan="2"></td>
                            </tr>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {/* Grand Total */}
                  <tr className="table-dark fw-bold" style={{ fontSize: '1.1rem', color: 'white' }}>
                    <td colSpan="3" className="text-end" style={{ color: 'white' }}>
                      {t(language, "revenueGrandTotal")}
                    </td>
                    <td className="text-end" style={{ color: 'white' }}>${grandTotal.toFixed(2)}</td>
                    <td colSpan="2" style={{ color: 'white' }}></td>
                  </tr>
                </>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  );
}
